import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { ActionIcon, Flex, Loader, Menu, Text } from '@mantine/core';
import React from 'react';
import type { TableRowActionGroups, TableRowActionOption } from './TableRowActionsMenu.types';

interface Props {
	options?: TableRowActionOption[];
	groups?: TableRowActionGroups;
	onMenuOpen?: () => void;
	onMenuClose?: () => void;
	notice?: string;
	noticeTestId?: string;
	menuTestId?: string;
}

const renderOption = (option: TableRowActionOption) => (
	<Menu.Item
		key={option.label}
		data-testid={option.dataTestId}
		data-loading={option.loading || undefined}
		onClick={(e: React.MouseEvent) => {
			e.stopPropagation();
			option.onClick();
		}}
		leftSection={option.icon ? <Icon variant={option.icon} size="lg" /> : undefined}
		rightSection={option.loading ? <Loader size="xs" /> : undefined}
		p="0.5rem"
		disabled={option.disabled || option.loading}
		style={{ opacity: option.disabled || option.loading ? 0.5 : 1 }}
	>
		<Text size="0.875rem" c="dark.9">
			{option.label}
		</Text>
	</Menu.Item>
);

export const TableRowActionsMenu = ({
	options,
	groups,
	onMenuOpen,
	onMenuClose,
	notice,
	noticeTestId,
	menuTestId,
}: Props) => {
	const orderedGroups: TableRowActionOption[][] = groups
		? [groups.primary, groups.secondary, groups.tertiary]
				.map((g) => g ?? [])
				.filter((g) => g.length > 0)
		: [options ?? []];

	return (
		<Flex justify="flex-end" w="100%">
			<Menu
				position="bottom-end"
				width="12.5rem"
				withinPortal
				onOpen={onMenuOpen}
				onClose={onMenuClose}
			>
				<Menu.Target>
					<ActionIcon
						variant="subtle"
						data-testid={menuTestId ?? TEST_ELEMENT_IDS.TABLE_ROW_ACTIONS_MENU}
						onClick={(e: React.MouseEvent) => e.stopPropagation()}
					>
						<Icon variant="more" size="lg" color={defaultTheme.v3.colors.black[0]} />
					</ActionIcon>
				</Menu.Target>
				<Menu.Dropdown
					p="0.25rem"
					onClick={(e: React.MouseEvent) => {
						e.stopPropagation();
						e.preventDefault();
					}}
				>
					{notice && (
						<Menu.Label
							p="0.5rem"
							fz={12}
							fw={400}
							c="gray.9"
							data-testid={noticeTestId}
						>
							{notice}
						</Menu.Label>
					)}
					{orderedGroups.map((group, idx) => (
						<React.Fragment key={idx}>
							{idx > 0 && <Menu.Divider />}
							{group.map(renderOption)}
						</React.Fragment>
					))}
				</Menu.Dropdown>
			</Menu>
		</Flex>
	);
};
